/**
 * DeleteConfirmModal — password re-entry + the full category summary.
 *
 * Two phases inside one modal:
 *   form       — what gets deleted + when + password → "Schedule deletion"
 *   scheduled  — confirmation: the date and that they can cancel any time.
 *
 * Copy is matter-of-fact, never panicky ("we hear you, here's exactly
 * what happens"). The single inline error is 401/password-mismatch →
 * "That password didn't match." Everything else is presentational —
 * the actual API call lives in useErasure.
 */

import { useEffect, useState } from "react";
import { Icon } from "../chrome/Icon";
import { PanelSurface } from "../chrome/PanelSurface";
import { ChromeTokens } from "../chrome/tokens";
import {
  type FamilySummary,
  erasureCategories,
} from "./erasureCategories";
import { GRACE_DAYS, useErasure } from "./useErasure";
import { PasswordMismatchError } from "./api";

interface Props {
  open: boolean;
  onClose: () => void;
  family: FamilySummary;
}

const tokens = ChromeTokens;

function previewScheduledDate(): string {
  const ms = Date.now() + GRACE_DAYS * 86_400_000;
  return new Date(ms).toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export function DeleteConfirmModal({ open, onClose, family }: Props): JSX.Element | null {
  const e = useErasure();
  const [pw, setPw] = useState("");
  const [show, setShow] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setPw("");
      setShow(false);
      setErr("");
      setBusy(false);
    }
  }, [open]);

  if (!open) return null;

  const scheduled = e.pending;

  const submit = async () => {
    if (!pw) {
      setErr("Enter your password to continue.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      await e.request(pw);
    } catch (caught) {
      if (caught instanceof PasswordMismatchError) {
        setErr("That password didn't match.");
      } else {
        setErr("Something went wrong. Please try again.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <PanelSurface
      open={open}
      onDismiss={onClose}
      ariaLabel={scheduled ? "Deletion scheduled" : "Delete your family account"}
      anchor="center"
    >
      <div style={{ width: 480, maxWidth: "calc(100vw - 32px)" }}>
        {scheduled
          ? <ScheduledPhase scheduledStr={e.scheduledStr} onClose={onClose} family={family} />
          : (
            <FormPhase
              family={family}
              pw={pw}
              setPw={(v) => {
                setPw(v);
                if (err) setErr("");
              }}
              show={show}
              setShow={setShow}
              err={err}
              busy={busy}
              submit={submit}
              onClose={onClose}
            />
          )}
      </div>
    </PanelSurface>
  );
}

function FormPhase(props: {
  family: FamilySummary;
  pw: string;
  setPw: (v: string) => void;
  show: boolean;
  setShow: (v: boolean | ((s: boolean) => boolean)) => void;
  err: string;
  busy: boolean;
  submit: () => void;
  onClose: () => void;
}): JSX.Element {
  const { family, pw, setPw, show, setShow, err, busy, submit, onClose } = props;
  const cats = erasureCategories(family);
  const dangerSoft = `color-mix(in srgb, ${tokens.color.danger} 12%, ${tokens.color.base})`;
  const dangerLine = `color-mix(in srgb, ${tokens.color.danger} 30%, ${tokens.color.baseMuted})`;

  return (
    <div>
      <h2
        style={{
          fontFamily: tokens.font.display,
          fontSize: 24,
          margin: `0 0 ${tokens.spacing.sm}px`,
          color: tokens.color.ink,
          lineHeight: 1.15,
        }}
      >
        Delete your family account
      </h2>
      <p style={{ fontSize: 14.5, lineHeight: 1.55, margin: `0 0 ${tokens.spacing.sm}px`, color: tokens.color.ink }}>
        This removes everything below — for your whole family. You'll have{" "}
        <strong>{GRACE_DAYS} days</strong> to change your mind before anything
        is permanently erased.
      </p>

      <div
        style={{
          marginTop: tokens.spacing.lg,
          marginBottom: tokens.spacing.xs,
          fontSize: 11.5,
          fontWeight: 800,
          letterSpacing: ".06em",
          textTransform: "uppercase",
          color: tokens.color.inkMuted,
        }}
      >
        What gets deleted
      </div>
      <ul
        style={{
          listStyle: "none",
          margin: 0,
          padding: `2px ${tokens.spacing.lg}px`,
          border: `1.5px solid ${dangerLine}`,
          borderRadius: tokens.radius.md,
          background: dangerSoft,
        }}
      >
        {cats.map((c, i) => (
          <li
            key={c.label}
            style={{
              display: "flex",
              gap: tokens.spacing.md,
              alignItems: "flex-start",
              padding: "11px 0",
              borderBottom: i === cats.length - 1 ? "none" : `1px solid ${tokens.color.baseMuted}`,
            }}
          >
            <span
              style={{
                width: 30,
                height: 30,
                borderRadius: 9,
                flexShrink: 0,
                display: "grid",
                placeItems: "center",
                background: `color-mix(in srgb, ${tokens.color.danger} 10%, ${tokens.color.base})`,
              }}
            >
              <Icon name={c.icon} size={16} stroke={tokens.color.danger} />
            </span>
            <span style={{ minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.3, color: tokens.color.ink }}>
                {c.label}
              </div>
              <div style={{ fontSize: 12.5, color: tokens.color.inkMuted, lineHeight: 1.4, marginTop: 1 }}>
                {c.detail}
              </div>
            </span>
          </li>
        ))}
      </ul>

      <div style={{ marginTop: tokens.spacing.md, display: "flex", flexDirection: "column", gap: tokens.spacing.sm }}>
        <QuietNote icon="database">
          We also tell the services that help run world by me — payments, voice
          and speech — to delete what they hold.
        </QuietNote>
        <QuietNote icon="share-2">
          Worlds other kids remixed from yours stay in their towns, shown as
          made by <em>someone who left</em>.
        </QuietNote>
        <QuietNote icon="shield">
          For safety, we keep the small set of moderation records described in
          our <strong>Privacy Notice</strong>.
        </QuietNote>
      </div>

      <div
        style={{
          marginTop: tokens.spacing.lg,
          padding: `12px ${tokens.spacing.lg}px`,
          borderRadius: tokens.radius.md,
          background: tokens.color.baseMuted,
          display: "flex",
          alignItems: "center",
          gap: tokens.spacing.md,
        }}
      >
        <Icon name="calendar-clock" size={17} stroke={tokens.color.clay} />
        <div style={{ fontSize: 13.5, lineHeight: 1.4, color: tokens.color.ink }}>
          Scheduled for <strong>{previewScheduledDate()}</strong> · cancel any time until then.
        </div>
      </div>

      <label
        style={{
          marginTop: tokens.spacing.lg,
          display: "flex",
          flexDirection: "column",
          gap: tokens.spacing.xs,
          fontSize: 13,
          fontWeight: 700,
          color: tokens.color.ink,
        }}
      >
        Enter your password to confirm
        <span style={{ position: "relative", display: "block" }}>
          <input
            type={show ? "text" : "password"}
            autoComplete="current-password"
            placeholder="Your password"
            value={pw}
            onChange={(ev) => setPw(ev.target.value)}
            onKeyDown={(ev) => { if (ev.key === "Enter") submit(); }}
            disabled={busy}
            style={{
              width: "100%",
              padding: `10px 44px 10px ${tokens.spacing.md}px`,
              fontSize: 14,
              fontFamily: tokens.font.ui,
              color: tokens.color.ink,
              background: tokens.color.base,
              border: `1.5px solid ${err ? tokens.color.danger : tokens.color.baseMuted}`,
              borderRadius: tokens.radius.sm,
              outline: "none",
              boxSizing: "border-box",
            }}
          />
          <button
            type="button"
            onClick={() => setShow((s) => !s)}
            aria-label={show ? "Hide password" : "Show password"}
            style={{
              position: "absolute",
              top: 0,
              bottom: 0,
              right: 8,
              margin: "auto",
              width: 30,
              height: 30,
              border: "none",
              background: "none",
              cursor: "pointer",
              color: tokens.color.inkMuted,
              display: "grid",
              placeItems: "center",
            }}
          >
            <Icon name={show ? "eye-off" : "eye"} size={17} />
          </button>
        </span>
        {err && (
          <span
            style={{
              fontSize: 12.5,
              color: tokens.color.danger,
              fontWeight: 700,
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
            }}
          >
            <Icon name="alert-triangle" size={13} stroke={tokens.color.danger} />
            {err}
          </span>
        )}
      </label>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: tokens.spacing.sm,
          marginTop: tokens.spacing.lg,
        }}
      >
        <button
          type="button"
          disabled={busy}
          onClick={submit}
          style={{
            width: "100%",
            padding: "11px 18px",
            background: tokens.color.danger,
            color: "#fdf6ec",
            border: "none",
            borderRadius: tokens.radius.pill,
            fontFamily: tokens.font.ui,
            fontWeight: 800,
            fontSize: 14.5,
            cursor: busy ? "wait" : "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: tokens.spacing.sm,
            boxShadow: tokens.shadow.soft,
          }}
        >
          <Icon name="trash-2" size={16} stroke="#fdf6ec" />
          {busy ? "Scheduling…" : "Schedule deletion"}
        </button>
        <button
          type="button"
          onClick={onClose}
          style={{
            width: "100%",
            padding: "11px 18px",
            background: tokens.color.baseMuted,
            color: tokens.color.ink,
            border: "none",
            borderRadius: tokens.radius.pill,
            fontFamily: tokens.font.ui,
            fontWeight: 700,
            fontSize: 14.5,
            cursor: "pointer",
          }}
        >
          Keep my account
        </button>
      </div>
    </div>
  );
}

function ScheduledPhase(props: {
  scheduledStr: string | null;
  onClose: () => void;
  family: FamilySummary;
}): JSX.Element {
  const { scheduledStr, onClose, family } = props;
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          padding: `${tokens.spacing.sm}px 0 ${tokens.spacing.xs}px`,
        }}
      >
        <span
          style={{
            width: 56,
            height: 56,
            borderRadius: 999,
            display: "grid",
            placeItems: "center",
            background: `color-mix(in srgb, ${tokens.color.grass} 26%, ${tokens.color.base})`,
            marginBottom: tokens.spacing.md,
          }}
        >
          <Icon name="calendar-clock" size={26} stroke="#3a6e3f" />
        </span>
        <h2
          style={{
            fontFamily: tokens.font.display,
            fontSize: 26,
            margin: `0 0 ${tokens.spacing.sm}px`,
            lineHeight: 1.1,
            color: tokens.color.ink,
          }}
        >
          You have until {scheduledStr ?? "the scheduled date"}
        </h2>
        <p
          style={{
            fontSize: 14,
            color: tokens.color.inkMuted,
            lineHeight: 1.55,
            margin: "0 auto",
            maxWidth: 360,
          }}
        >
          Nothing is gone yet. Your family's worlds stay exactly as they are
          for the next <strong>{GRACE_DAYS} days</strong>. On that day everything
          is permanently erased.
        </p>
      </div>
      <div
        style={{
          marginTop: tokens.spacing.lg,
          padding: `13px ${tokens.spacing.lg}px`,
          borderRadius: tokens.radius.md,
          background: `color-mix(in srgb, ${tokens.color.sky} 22%, ${tokens.color.base})`,
          display: "flex",
          gap: tokens.spacing.md,
          alignItems: "flex-start",
        }}
      >
        <Icon name="info" size={17} stroke={tokens.color.clay} style={{ flexShrink: 0, marginTop: 1 }} />
        <div style={{ fontSize: 13, lineHeight: 1.5, color: tokens.color.ink }}>
          Changed your mind? You can cancel any time before {scheduledStr ?? "the scheduled date"} from
          the banner at the top of the app, or right here in{" "}
          <strong>Manage subscription</strong>.
          {family.plan ? <> A copy of this is on its way to your inbox.</> : null}
        </div>
      </div>
      <div style={{ marginTop: tokens.spacing.lg }}>
        <button
          type="button"
          onClick={onClose}
          style={{
            width: "100%",
            padding: "11px 18px",
            background: tokens.color.sun,
            color: tokens.color.ink,
            border: "none",
            borderRadius: tokens.radius.pill,
            fontFamily: tokens.font.ui,
            fontWeight: 800,
            fontSize: 14.5,
            cursor: "pointer",
            boxShadow: tokens.shadow.soft,
          }}
        >
          Done
        </button>
      </div>
    </div>
  );
}

function QuietNote({ icon, children }: { icon: "database" | "share-2" | "shield"; children: React.ReactNode }): JSX.Element {
  return (
    <div style={{ display: "flex", gap: 9, alignItems: "flex-start" }}>
      <Icon name={icon} size={14} stroke={tokens.color.inkMuted} style={{ flexShrink: 0, marginTop: 2 }} />
      <div style={{ fontSize: 12.5, color: tokens.color.inkMuted, lineHeight: 1.45 }}>{children}</div>
    </div>
  );
}
